/**
 * The first choice after signing in: what kind of work is this?
 *
 * Shown before any project list, because accounting and valuation have different
 * projects, different tabs and different degrees of assurance - the accounting
 * engine is checked line by line against Mike's workbook, the valuation engine has
 * no reference to check against at all. Asking first is what makes that separation
 * real rather than a pair of headings on one crowded screen.
 *
 * Two oversized cards rather than a menu: this is the first thing a user sees and
 * there are two ways to go, so the choice should be unmissable and take one click
 * from anywhere on the card.
 *
 * Icons are inline SVG, not image files. They are two shapes each, they inherit the
 * text colour on hover, and they stay sharp at the size used here - none of which a
 * logo file manages. `ICONS` is keyed by discipline with a fallback, so the two
 * further disciplines expected render sensibly before anyone draws them one.
 */

import type { ReactNode } from 'react';

import { AccountMenuV2 } from './AccountMenuV2';
import { DISCIPLINES, type DisciplineId } from './disciplines';

/** Scales and a ledger: the balance sheet balances, which is the whole discipline. */
function AccountingIcon() {
  return (
    <svg viewBox="0 0 48 48" fill="none" className="h-6 w-6" aria-hidden="true">
      <path
        d="M24 6v30M10 14h28M14 14l-6 12h12l-6-12ZM34 14l-6 12h12l-6-12Z"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M16 40h16" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

/** A rising line over an axis: what the business is worth, over time. */
function ValuationIcon() {
  return (
    <svg viewBox="0 0 48 48" fill="none" className="h-6 w-6" aria-hidden="true">
      <path
        d="M8 40V8M8 40h32"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <path
        d="M14 32l8-9 6 5 10-14"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M32 14h6v6"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** A ledger: rows and columns, as the baseline model is laid out. */
function BaselineIcon() {
  return (
    <svg viewBox="0 0 48 48" fill="none" className="h-6 w-6" aria-hidden="true">
      <rect x="7" y="8" width="34" height="32" rx="3" stroke="currentColor" strokeWidth="3" />
      <path d="M7 18h34M7 29h34M20 8v32" stroke="currentColor" strokeWidth="3" />
    </svg>
  );
}

/** A plain document, for a discipline that has not been given an icon yet. */
function GenericIcon() {
  return (
    <svg viewBox="0 0 48 48" fill="none" className="h-6 w-6" aria-hidden="true">
      <path
        d="M12 6h16l8 8v28H12V6Z"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinejoin="round"
      />
      <path
        d="M28 6v8h8M18 26h12M18 33h12"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

function DiligenceIcon() {
  // A magnifier over a page: findings read from the documents.
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" aria-hidden="true">
      <path d="M6 3h8l4 4v6" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M6 3v18h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="16" cy="16" r="3.2" stroke="currentColor" strokeWidth="1.8" />
      <path d="M18.4 18.4 21 21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function BooksIcon() {
  // A ruled ledger page with a tick: the books, kept and proved.
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" aria-hidden="true">
      <rect x="4" y="3" width="16" height="18" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
      <path d="M8 8h8M8 12h4M8 16h3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M13.5 15.5l1.6 1.6 3-3.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const ICONS: Partial<Record<DisciplineId, ReactNode>> = {
  accounting: <AccountingIcon />,
  valuation: <ValuationIcon />,
  'm-accounting': <BaselineIcon />,
  diligence: <DiligenceIcon />,
  books: <BooksIcon />,
};

export default function DisciplineChooser({
  onChoose,
}: {
  onChoose: (id: DisciplineId) => void;
}) {
  return (
    <div className="min-h-screen bg-canvas">
    <div className="mx-auto max-w-5xl px-4 py-14 sm:px-8 sm:py-20">
      {/* The account menu here too: this is the page a sign-in lands on, so signing out or
          changing a password should not need a workspace opened first. */}
      <div className="flex items-start justify-between gap-4">
        <h1 className="font-display text-4xl font-extrabold text-gray-900">Select a workspace</h1>
        <AccountMenuV2 />
      </div>
      <p className="mt-2 text-sm text-gray-600">
        IAS accounting, valuation, baseline accounting, due diligence and books each keep their own projects and
        audit trail. Move between them at any time with Change workspace.
      </p>

      <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {DISCIPLINES.map((discipline) => (
          <button
            key={discipline.id}
            type="button"
            // Each card in its own discipline's colour: the colour that will mark
            // every screen of it from here on.
            data-discipline={discipline.id}
            onClick={() => onChoose(discipline.id)}
            className="group relative flex flex-col items-start overflow-hidden rounded-2xl border border-gray-200 bg-white p-8 text-left shadow-[0_1px_2px_rgb(var(--c-black)/0.04)] transition hover:-translate-y-0.5 hover:border-accent-300 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-600"
          >
            <span aria-hidden="true" className="absolute inset-x-0 top-0 h-1 bg-accent-600" />
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent-50 text-accent-600 ring-1 ring-inset ring-accent-200">
              {ICONS[discipline.id] ?? <GenericIcon />}
            </span>
            <span className="mt-6 font-display text-xl font-bold text-gray-900 group-hover:text-accent-700">
              {discipline.label}
            </span>
            <span className="mt-2 text-sm leading-relaxed text-gray-600">
              {discipline.blurb}
            </span>
            <span className="mt-auto pt-6 text-sm font-semibold text-accent-700">
              Open {discipline.label} <span aria-hidden="true">&rarr;</span>
            </span>
          </button>
        ))}
      </div>
    </div>
    </div>
  );
}
